import { Cache as ThreeCache, LoadingManager, Object3D, Texture } from 'three';

import type { ModelManagerConfig, ModelLoadOptions, ModelLoadCallbacks } from './types';
import { loadGLTFCfn } from './functions/load-gltf.cfn';
import { loadColladaCfn } from './functions/load-collada.cfn';
import { applyEnvironmentMapToModel, applyWireframeToModel, retryWithBackoff } from './helpers';
import { ModelLoadError } from '../../types/export-errors';

/**
 * ModelManager handles loading and managing 3D models:
 * - GLTF/GLB model loading
 * - Collada (DAE) model loading
 * - Material replacement
 * - Model caching
 * - Shadow configuration
 */
export class ModelManager {
	#config: ModelManagerConfig;
	/** SKUs whose model file 404s permanently — rendered without a model and pruned from fidelity manifests */
	#missingModelSkus = new Set<string>();
	/** HEAD-probe results per path so repeated builds don't re-probe */
	#missingProbeCache = new Map<string, boolean>();
	#gltfCache = new Map<string, Object3D>();
	#colladaCache = new Map<string, Object3D>();
	#loadedModels = new Map<string, Object3D>();
	#wireframeMode = false;
	#environmentMap: Texture | null = null;

	constructor(config: ModelManagerConfig) {
		this.#config = config;
		this.#wireframeMode = config.qualitySettings?.wireframe ?? false;
	}

	// ============ Public Getters (for CFN files) ============

	/** Access config */
	get config(): ModelManagerConfig {
		return this.#config;
	}

	/** Access wireframe mode */
	get wireframeMode(): boolean {
		return this.#wireframeMode;
	}

	/** Access environment map */
	get environmentMap(): Texture | null {
		return this.#environmentMap;
	}

	/** Access loaded models map */
	get loadedModels(): Map<string, Object3D> {
		return this.#loadedModels;
	}

	/** Access GLTF cache */
	get gltfCache(): Map<string, Object3D> {
		return this.#gltfCache;
	}

	/** Access Collada cache */
	get colladaCache(): Map<string, Object3D> {
		return this.#colladaCache;
	}

	/**
	 * Set environment map for all loaded and future models
	 */
	setEnvironmentMap(envMap: Texture | null): void {
		this.#environmentMap = envMap;
		this.#loadedModels.forEach((model) => {
			applyEnvironmentMapToModel(model, envMap);
		});
	}

	/**
	 * Set wireframe mode for all loaded models
	 */
	setWireframe(enabled: boolean): void {
		this.#wireframeMode = enabled;
		this.#loadedModels.forEach((model) => {
			applyWireframeToModel(model, enabled);
		});
	}

	/**
	 * Update configuration
	 */
	updateConfig(config: Partial<ModelManagerConfig>): void {
		this.#config = { ...this.#config, ...config };
	}

	/**
	 * Get a loaded model by ID
	 */
	getModel(id: string): Object3D | undefined {
		return this.#loadedModels.get(id);
	}

	/**
	 * Check if a model is cached
	 */
	isCached(path: string): boolean {
		return this.#gltfCache.has(path) || this.#colladaCache.has(path);
	}

	/**
	 * Load a GLTF/GLB model
	 */
	async loadGLTF(options: ModelLoadOptions): Promise<Object3D> {
		return loadGLTFCfn(this, { options });
	}

	/**
	 * Load a Collada (DAE) model
	 */
	async loadCollada(options: ModelLoadOptions): Promise<Object3D> {
		return loadColladaCfn(this, { options });
	}

	/**
	 * Load multiple models with retry-with-backoff per model.
	 * strict=true throws ModelLoadError after retries exhaust on any path.
	 * onFailures callback receives a summary of all paths that failed after retries.
	 */
	/** @internal SKUs whose model files are permanently absent (HTTP 404). */
	get missingModelSkusAccess (): Set<string> {
		return this.#missingModelSkus;
	}

	async #isPermanentlyMissing (path: string): Promise<boolean> {
		const cached = this.#missingProbeCache.get(path);
		if (cached !== undefined) return cached;
		const url = path.startsWith('/') || path.startsWith('http') ? path : `${this.#config.basePath}${path}`;
		let missing = false;
		try {
			const response = await fetch(url, { method: 'HEAD' });
			missing = response.status === 404;
		} catch {
			missing = false;
		}
		this.#missingProbeCache.set(path, missing);
		return missing;
	}

	async loadModels(
		models: ModelLoadOptions[],
		callbacks: ModelLoadCallbacks & { onFailures?: (failures: Array<{ path: string; error: string }>) => void } = {},
		{ strict = false }: { strict?: boolean } = {}
	): Promise<Object3D[]> {
		const loadingManager = new LoadingManager();
		const results: Object3D[] = [];
		const failures: Array<{ path: string; error: string }> = [];

		loadingManager.onProgress = (_url, loaded, total) => {
			callbacks.onProgress?.(loaded, total);
		};

		loadingManager.onError = (url) => {
			// Evict the failed entry — Three caches the broken Image/response and every retry (even from a fresh engine) would reuse the corpse
			ThreeCache.remove(url);
			callbacks.onError?.(url);
		};

		const promises = models.map(async (options) => {
			try {
				const pathLower = options.path.toLowerCase();
				const model = await retryWithBackoff(async () => {
					if (pathLower.endsWith('.gltf') || pathLower.endsWith('.glb')) {
						return this.loadGLTF(options);
					} else if (pathLower.endsWith('.dae')) {
						return this.loadCollada(options);
					}
					throw new Error(`Unsupported model format: ${options.path}`);
				});
				results.push(model);
			} catch (error) {
				// A permanent 404 is a data gap, not a build failure — render without the model and
				// record the sku so fidelity manifests exclude it. Everything else stays strict.
				if (await this.#isPermanentlyMissing(options.path)) {
					const sku = String(options.metadata?.sku ?? options.path);
					this.#missingModelSkus.add(sku);
					console.warn(`[ModelManager] model file missing (404), rendering without it: ${options.path} (sku=${sku})`);
					return;
				}
				const msg = error instanceof Error ? error.message : String(error);
				failures.push({ path: options.path, error: msg });
			}
		});

		await Promise.all(promises);

		if (failures.length > 0) {
			console.error(`[ModelManager] ${failures.length} model(s) failed after retries:`, failures.map((f) => f.path).join(', '));
			callbacks.onFailures?.(failures);
			if (strict) {
				throw new ModelLoadError(
					`Model load failed for: ${failures.map((f) => f.path).join(', ')}`,
					failures.map((f) => f.path)
				);
			}
		}

		callbacks.onComplete?.();
		return results;
	}

	/**
	 * Remove a loaded model
	 */
	removeModel(id: string): void {
		const model = this.#loadedModels.get(id);
		if (model) {
			if (model.parent) {
				model.parent.remove(model);
			}
			this.#loadedModels.delete(id);
		}
	}

	/**
	 * Clear model cache
	 */
	clearCache(): void {
		this.#gltfCache.clear();
		this.#colladaCache.clear();
	}

	/**
	 * Dispose of all resources
	 */
	dispose(): void {
		this.#loadedModels.clear();
		this.clearCache();
	}
}
